import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Upload, FileText, Flag, RefreshCw, Plus, Trash2, Wand2, Download, Check, ArrowRight, Sparkles } from "lucide-react";
import { PHASES } from "@/data/diet";
import { FoodTracker } from "@/components/admin/FoodTracker";
import { GlobalCuration } from "@/components/admin/GlobalCuration";

const PHASE_STARTS = [1, 16, 31, 61, 91, 121, 151, 181, 211, 241, 271, 301];
const STATUS_DOT = {
  active: "#34A853",
  pending_onboarding: "#F5B70A",
  paused: "#9AA294",
  completed: "#2D372B",
  cancelled: "#A05252",
};
const fmtShort = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "2-digit" });

const PricePlansCard = ({ api }) => {
  const [progs, setProgs] = useState(null);
  const [cur, setCur] = useState("india");
  const [dl, setDl] = useState(false);
  const ORDER_IDS = ["jaytrial", "jay15", "jay30", "jay90", "jay180", "jay365", "jay365pro"];

  useEffect(() => {
    api().get("/diet/programmes").then((r) => setProgs(r.data.programmes || {})).catch(() => {});
  }, []); // eslint-disable-line

  const downloadPdf = async () => {
    setDl(true);
    try {
      const r = await api().get("/admin/diet/price-plan-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "JAY_DIETS_Price_Plans.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate the price plan PDF"); } finally { setDl(false); }
  };

  const label = (p) => {
    if (p.trial) return "FREE";
    if (p.enquire && cur === "intl") return "Enquire";
    return cur === "india" ? `₹${Number(p.inr).toLocaleString("en-IN")}` : `US$ ${p.usd}`;
  };

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5 mb-5" data-testid="admin-price-plans">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="font-medium">Price Plans (admin reference)</div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-[#E5E2D9] bg-[#F7F5F0] p-0.5" data-testid="admin-currency-toggle">
            {[["india", "India (₹)"], ["intl", "International ($)"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`admin-currency-${v}`} onClick={() => setCur(v)}
                className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${cur === v ? "bg-[#2D372B] text-white" : "text-[#5C665A]"}`}>
                {l}
              </button>
            ))}
          </div>
          <button data-testid="price-plan-pdf-btn" onClick={downloadPdf} disabled={dl}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium bg-[#C57B57] text-white hover:bg-[#AD6A48] disabled:opacity-60">
            {dl ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} Price Plan PDF
          </button>
        </div>
      </div>
      {progs && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {ORDER_IDS.filter((id) => progs[id]).map((id) => (
            <div key={id} className="rounded-xl border border-[#F0EEE6] bg-[#FAF9F4] px-3 py-2.5" data-testid={`plan-price-${id}`}>
              <div className="text-[10px] uppercase tracking-wider text-[#9AA294]">{progs[id].name}</div>
              <div className="font-serif-display text-lg mt-0.5">{label(progs[id])}</div>
              <div className="text-[10px] text-[#9AA294]">{progs[id].days} days</div>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-[#9AA294] mt-3">Visitors see prices automatically in their local currency (geo-detected). India → ₹ as-is; everywhere else → US$ converted (AED, KWD, SGD, EUR, GBP…).</p>
    </div>
  );
};

const MEAL_ROW = { meal: "", food: "", quantity: "", calories: "", protein: "", carbs: "", fat: "", alternatives: "" };
const inCls = "border border-[#E5E2D9] rounded-lg px-2 py-1.5 text-xs bg-[#F7F5F0] w-full";

const Jay7Builder = ({ api, cid, onDone }) => {
  const [f, setF] = useState({ week: "1", phase: "", objective: "", exchanges: "", portion_guide: "", hydration: "", eating_out: "", notes: "", weekly_objective: "" });
  const [meals, setMeals] = useState([
    { ...MEAL_ROW, meal: "Breakfast" }, { ...MEAL_ROW, meal: "Lunch" }, { ...MEAL_ROW, meal: "Snack" }, { ...MEAL_ROW, meal: "Dinner" },
  ]);
  const [busy, setBusy] = useState(false);
  const [exGroups, setExGroups] = useState([]);
  const [exSel, setExSel] = useState({ group: "", item: "" });
  const [exItems, setExItems] = useState([]);
  const [exLoading, setExLoading] = useState(true);
  const [drafting, setDrafting] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setMeal = (i, k, v) => setMeals(meals.map((m, j) => (j === i ? { ...m, [k]: v } : m)));

  useEffect(() => {
    api().get("/admin/diet/food-exchanges")
      .then((r) => setExGroups(r.data.groups || []))
      .catch(() => toast.error("Could not load the Food Exchange List — refresh to retry"))
      .finally(() => setExLoading(false));
  }, []); // eslint-disable-line

  const addExchange = () => {
    const g = exGroups.find((x) => x.id === exSel.group);
    const it = g?.items.find((x) => x.name === exSel.item);
    if (!it) { toast.error("Pick an exchange group and a food"); return; }
    if (exItems.some((x) => x.name === it.name && x.group === g.name)) { toast.error("Already added"); return; }
    setExItems([...exItems, {
      group: g.name.replace(/ Exchange.*$/, ""), name: it.name,
      amount: `${it.amount_g} g${it.measure ? ` (${it.measure})` : ""}`,
      kcal: String(it.kcal), protein: String(it.protein), carb: String(it.carb), fat: String(it.fat),
    }]);
  };

  const draftWithAI = async () => {
    setDrafting(true);
    try {
      const r = await api().post(`/admin/diet/clients/${cid}/draft-jay7`, {}, { timeout: 120000 });
      const d = r.data.draft || {};
      setF({
        week: d.week || "1", phase: d.phase || "", objective: d.objective || "",
        exchanges: d.exchanges || "", portion_guide: d.portion_guide || "", hydration: d.hydration || "",
        eating_out: d.eating_out || "", notes: d.notes || "", weekly_objective: d.weekly_objective || "",
      });
      if ((d.meals || []).length) setMeals(d.meals.map((m) => ({ ...MEAL_ROW, ...m })));
      if ((d.exchange_items || []).length) setExItems(d.exchange_items);
      toast.success("AI draft ready — review and edit before generating the PDF");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI drafting failed — please try again");
    } finally { setDrafting(false); }
  };

  const generate = async () => {
    const valid = meals.filter((m) => m.meal.trim() && m.food.trim());
    if (!f.week.trim()) { toast.error("Enter the week number"); return; }
    if (valid.length === 0) { toast.error("Add at least one meal with food"); return; }
    setBusy(true);
    try {
      await api().post(`/admin/diet/clients/${cid}/generate-jay7`, { ...f, meals: valid, exchange_items: exItems });
      toast.success("JAY 7™ PDF generated & client emailed");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5" data-testid="jay7-builder">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="font-medium flex items-center gap-2"><Wand2 size={15} className="text-[#AD6A48]" /> Create JAY 7™ (premium PDF)</div>
        <button type="button" data-testid="jay7-ai-draft-btn" onClick={draftWithAI} disabled={drafting || busy}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium bg-[#2D372B] text-[#E8C9B8] hover:bg-[#3D473B] disabled:opacity-60">
          {drafting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {drafting ? "Drafting from assessment…" : "Draft with AI"}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <input data-testid="jay7-week" value={f.week} onChange={set("week")} placeholder="Week *" className={inCls} />
        <input value={f.phase} onChange={set("phase")} placeholder="Phase (e.g. ESTABLISH)" className={inCls} />
        <input value={f.weekly_objective} onChange={set("weekly_objective")} placeholder="This week's objective" className={inCls} />
      </div>
      <textarea data-testid="jay7-objective" rows={2} value={f.objective} onChange={set("objective")} placeholder="Current objective (shown on page 2)" className={`${inCls} mt-2 resize-none`} />
      <div className="mt-3 space-y-2 overflow-x-auto">
        <div className="grid gap-1 min-w-[640px]" style={{ gridTemplateColumns: "1.2fr 1.8fr 1fr 0.7fr 0.6fr 0.6fr 0.6fr 1.6fr 24px" }}>
          {["Meal", "Food", "Qty", "Kcal", "P", "C", "F", "Alternatives", ""].map((h, i) => (
            <span key={i} className="text-[10px] uppercase tracking-wider text-[#9AA294]">{h}</span>
          ))}
        </div>
        {meals.map((m, i) => (
          <div key={i} className="grid gap-1 min-w-[640px]" style={{ gridTemplateColumns: "1.2fr 1.8fr 1fr 0.7fr 0.6fr 0.6fr 0.6fr 1.6fr 24px" }} data-testid={`jay7-meal-row-${i}`}>
            {["meal", "food", "quantity", "calories", "protein", "carbs", "fat", "alternatives"].map((k) => (
              <input key={k} data-testid={k === "meal" || k === "food" ? `jay7-${k}-${i}` : undefined} value={m[k]} onChange={(e) => setMeal(i, k, e.target.value)} className={inCls} />
            ))}
            <button type="button" onClick={() => setMeals(meals.filter((_, j) => j !== i))} className="text-[#A05252]" aria-label="Remove row"><Trash2 size={13} /></button>
          </div>
        ))}
        <button type="button" data-testid="jay7-add-meal" onClick={() => setMeals([...meals, { ...MEAL_ROW }])} className="inline-flex items-center gap-1 text-xs text-[#5C665A] hover:text-[#2D372B]"><Plus size={12} /> Add meal row</button>
      </div>
      <div className="mt-4 border-t border-[#F0EEE6] pt-3">
        <div className="text-xs font-medium text-[#2D372B] mb-2">Curate from the JAY Food Exchange List</div>
        <div className="flex flex-wrap gap-2 items-center">
          <select data-testid="exchange-group-select" value={exSel.group} onChange={(e) => setExSel({ group: e.target.value, item: "" })} className={`${inCls} !w-48`} disabled={exLoading}>
            <option value="">{exLoading ? "Loading exchange list…" : "Exchange group…"}</option>
            {exGroups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <select data-testid="exchange-item-select" value={exSel.item} onChange={(e) => setExSel({ ...exSel, item: e.target.value })} className={`${inCls} !w-64`} disabled={!exSel.group}>
            <option value="">Food…</option>
            {(exGroups.find((g) => g.id === exSel.group)?.items || []).map((it) => (
              <option key={it.name} value={it.name}>{`${it.name} — ${it.amount_g}g · ${it.kcal} kcal`}</option>
            ))}
          </select>
          <button type="button" data-testid="exchange-add-btn" onClick={addExchange} className="inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-medium bg-[#2D372B] text-white"><Plus size={11} /> Add</button>
        </div>
        {exItems.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="exchange-chips">
            {exItems.map((x, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-[#89937C]/15 text-[#3D473B] text-[11px] px-3 py-1">
                {x.name} · {x.amount} · {x.kcal} kcal
                <button type="button" onClick={() => setExItems(exItems.filter((_, j) => j !== i))} className="text-[#A05252]" aria-label="Remove exchange"><Trash2 size={10} /></button>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <textarea rows={2} value={f.exchanges} onChange={set("exchanges")} placeholder="Food exchanges" className={`${inCls} resize-none`} />
        <textarea rows={2} value={f.portion_guide} onChange={set("portion_guide")} placeholder="Portion guide" className={`${inCls} resize-none`} />
        <textarea rows={2} value={f.hydration} onChange={set("hydration")} placeholder="Hydration" className={`${inCls} resize-none`} />
        <textarea rows={2} value={f.eating_out} onChange={set("eating_out")} placeholder="Eating-out guidance" className={`${inCls} resize-none`} />
      </div>
      <textarea rows={2} value={f.notes} onChange={set("notes")} placeholder="Notes" className={`${inCls} mt-2 resize-none`} />
      <button data-testid="jay7-generate-btn" onClick={generate} disabled={busy} className="mt-3 inline-flex items-center gap-2 rounded-full px-5 py-2 bg-[#C57B57] text-white text-sm font-medium hover:bg-[#AD6A48] disabled:opacity-60">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Generate JAY 7™ PDF & notify client
      </button>
    </div>
  );
};

const badge = "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium";
const statusCls = {
  pending_onboarding: "bg-[#C57B57]/15 text-[#AD6A48]",
  active: "bg-[#89937C]/15 text-[#5C665A]",
  completed: "bg-[#2D372B]/10 text-[#2D372B]",
  paused: "bg-[#E5E2D9] text-[#5C665A]",
  cancelled: "bg-[#A05252]/10 text-[#A05252]",
};

export const DietAdmin = ({ api }) => {
  const [clients, setClients] = useState(null);
  const [stats, setStats] = useState(null);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [up, setUp] = useState({ title: "JAY 7™ Week 1", doc_type: "jay7", phase: "", week: "", file: null });
  const [note, setNote] = useState({ what_changed: "", why: "" });
  const [review, setReview] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api().get("/admin/diet/clients").then((r) => setClients(r.data)).catch(() => toast.error("Could not load JAY clients"));
    api().get("/admin/diet/trial-stats").then((r) => setStats(r.data)).catch(() => {});
  };
  const loadDetail = (cid) => api().get(`/admin/diet/clients/${cid}`).then((r) => { setDetail(r.data); setReview(r.data.next_review ? r.data.next_review.slice(0, 10) : ""); });

  useEffect(() => { load(); }, []); // eslint-disable-line

  const open = (c) => { setSel(c.id); setDetail(null); loadDetail(c.id); };

  const upload = async () => {
    if (!up.file || !up.title.trim()) { toast.error("Choose a PDF and title"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", up.title); fd.append("doc_type", up.doc_type);
      if (up.phase) fd.append("phase", up.phase);
      if (up.week) fd.append("week", up.week);
      fd.append("file", up.file);
      await api().post(`/admin/diet/clients/${sel}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setUp({ ...up, file: null });
      loadDetail(sel);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!note.what_changed.trim()) { toast.error("Enter what changed"); return; }
    try {
      await api().post(`/admin/diet/clients/${sel}/note`, note);
      toast.success("Adaptation recorded");
      setNote({ what_changed: "", why: "" });
      loadDetail(sel);
    } catch { toast.error("Could not save note"); }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await api().get(`/admin/diet/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.title.replace(/ /g, "_")}_v${doc.version}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const update = async (payload) => {
    try {
      await api().post(`/admin/diet/clients/${sel}/update`, payload);
      toast.success("Updated");
      loadDetail(sel); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  if (!clients) return <div className="py-10 text-center"><Loader2 className="animate-spin inline text-[#89937C]" /></div>;

  if (sel && detail) {
    const d = detail;
    return (
      <div className="space-y-5" data-testid="diet-admin-detail">
        <button onClick={() => { setSel(null); setDetail(null); load(); }} data-testid="diet-admin-back" className="inline-flex items-center gap-2 text-sm text-[#5C665A] hover:text-[#2D372B]">
          <ArrowLeft size={14} /> All JAY clients
        </button>
        <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-serif-display text-3xl tracking-tight uppercase" data-testid="diet-detail-title">
                {d.name} — {d.programme?.name}
                {d.programme?.trial && <span className={`${badge} bg-[#89937C]/20 text-[#5C665A] ml-3 align-middle normal-case`}>TRIAL</span>}
                <span className={`${badge} ${statusCls[d.status] || ""} ml-2 align-middle normal-case`}>{d.status}</span>
                {d.professional_review && <span className={`${badge} bg-[#A05252]/10 text-[#A05252] ml-2 align-middle normal-case`}><Flag size={10} className="mr-1" /> professional review</span>}
              </div>
              {d.day ? <div className="font-medium text-lg mt-1" data-testid="diet-detail-day">Day {d.day} / {d.total_days}</div> : <div className="text-sm text-[#5C665A] mt-1">Awaiting onboarding</div>}
              <div className="text-sm text-[#5C665A] mt-1">{d.email} · {d.phone || "—"}{d.country ? ` · ${d.country}` : ""}</div>
              {d.day && (
                <div className="mt-4 space-y-1.5" data-testid="phase-checklist">
                  {PHASES.filter((_, i) => PHASE_STARTS[i] <= (d.total_days || 7)).map((ph, i, arr) => {
                    const curI = arr.findIndex((x) => x.name === d.phase?.name);
                    const st = i < curI ? "done" : i === curI ? "current" : "next";
                    return (
                      <div key={ph.n} className={`flex items-center gap-2 text-sm ${st === "current" ? "font-semibold text-[#2D372B]" : st === "done" ? "text-[#5C665A]" : "text-[#9AA294]"}`}>
                        <span className="uppercase tracking-wide">{ph.name}</span>
                        {st === "done" && <Check size={13} className="text-[#34A853]" />}
                        {st === "current" && <span className="inline-flex items-center gap-1 text-[#AD6A48]"><ArrowRight size={13} /> CURRENT</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={`https://wa.me/${(d.phone || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="rounded-full px-4 py-2 text-xs font-medium bg-[#25D366] text-white">WhatsApp</a>
              <a href={`mailto:${d.email}`} className="rounded-full px-4 py-2 text-xs font-medium border border-[#E5E2D9]">Email</a>
              <button data-testid="diet-flag-btn" onClick={() => update({ professional_review: !d.professional_review })} className="rounded-full px-4 py-2 text-xs font-medium border border-[#A05252]/40 text-[#A05252]">
                {d.professional_review ? "Unflag" : "Flag for professional review"}
              </button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-[#5C665A]">Next review:</span>
            <input type="date" value={review} onChange={(e) => setReview(e.target.value)} data-testid="diet-review-date" className="border border-[#E5E2D9] rounded-lg px-2 py-1.5 text-sm bg-[#F7F5F0]" />
            <button onClick={() => review && update({ next_review: new Date(review).toISOString() })} className="rounded-full px-4 py-1.5 text-xs font-medium bg-[#2D372B] text-white">Set</button>
            <span className="text-[#5C665A] ml-3">Status:</span>
            {["active", "paused", "completed"].map((s) => (
              <button key={s} onClick={() => update({ status: s })} className={`rounded-full px-3 py-1.5 text-xs border ${d.status === s ? "bg-[#2D372B] text-white border-[#2D372B]" : "border-[#E5E2D9]"}`}>{s}</button>
            ))}
            <span className="text-[#5C665A] ml-3">Phase:</span>
            <select
              data-testid="diet-phase-select"
              value={d.phase_override || "auto"}
              onChange={(e) => update({ phase_override: e.target.value })}
              className="border border-[#E5E2D9] rounded-lg px-2 py-1.5 text-sm bg-[#F7F5F0]"
            >
              <option value="auto">Auto (by day{d.phase?.name && !d.phase_manual ? ` — ${d.phase.name}` : ""})</option>
              {PHASES.map((ph) => (
                <option key={ph.n} value={ph.name.toLowerCase().replace(/ /g, "_")}>{ph.name} · {ph.days}</option>
              ))}
            </select>
            {d.phase_manual && <span className="text-[10px] uppercase tracking-wide text-[#AD6A48] font-semibold" data-testid="phase-manual-badge">Manual</span>}
          </div>
        </div>

        <Jay7Builder api={api} cid={sel} onDone={() => loadDetail(sel)} />

        <div className="grid lg:grid-cols-2 gap-5">
          <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5">
            <div className="font-medium mb-3 flex items-center gap-2"><Upload size={15} /> Upload JAY 7™ / document (PDF)</div>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="diet-upload-title" value={up.title} onChange={(e) => setUp({ ...up, title: e.target.value })} placeholder="Title (e.g. JAY 7™ Week 2)" className="col-span-2 border border-[#E5E2D9] rounded-lg px-3 py-2 text-sm bg-[#F7F5F0]" />
              <select value={up.doc_type} onChange={(e) => setUp({ ...up, doc_type: e.target.value })} className="border border-[#E5E2D9] rounded-lg px-2 py-2 text-sm bg-[#F7F5F0]">
                {["jay7", "exchange_guide", "progress_review", "phase_review", "maintenance", "other"].map((t) => <option key={t}>{t}</option>)}
              </select>
              <input value={up.phase} onChange={(e) => setUp({ ...up, phase: e.target.value })} placeholder="Phase (optional)" className="border border-[#E5E2D9] rounded-lg px-3 py-2 text-sm bg-[#F7F5F0]" />
              <input data-testid="diet-upload-file" type="file" accept="application/pdf" onChange={(e) => setUp({ ...up, file: e.target.files?.[0] || null })} className="col-span-2 text-sm" />
            </div>
            <button data-testid="diet-upload-btn" onClick={upload} disabled={busy} className="mt-3 inline-flex items-center gap-2 rounded-full px-5 py-2 bg-[#2D372B] text-white text-sm font-medium disabled:opacity-60">
              {busy && <Loader2 size={13} className="animate-spin" />} Upload
            </button>
            <ul className="mt-4 space-y-2">
              {(d.documents || []).map((doc) => (
                <li key={doc.id} data-testid="diet-doc-item" className="flex items-center gap-2 text-sm text-[#5C665A]">
                  <FileText size={14} className="text-[#AD6A48]" /> {doc.title} v{doc.version} · {new Date(doc.created_at).toLocaleDateString()}
                  <button type="button" data-testid="diet-doc-download" onClick={() => downloadDoc(doc)} className="ml-auto inline-flex items-center gap-1 text-xs text-[#AD6A48] hover:text-[#2D372B]" aria-label={`Download ${doc.title}`}>
                    <Download size={12} /> PDF
                  </button>
                  <button
                    type="button"
                    data-testid="diet-doc-delete"
                    onClick={async () => {
                      if (!window.confirm(`Delete "${doc.title} v${doc.version}"?\n\nIt will disappear from the client's dashboard too. This cannot be undone.`)) return;
                      try {
                        await api().delete(`/admin/diet/documents/${doc.id}`);
                        toast.success(`Deleted ${doc.title} v${doc.version}`);
                        loadDetail(sel);
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Could not delete document");
                      }
                    }}
                    className="inline-flex items-center gap-1 text-xs text-[#A05252] hover:text-[#7A3232]"
                    aria-label={`Delete ${doc.title}`}
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5">
            <div className="font-medium mb-3">Adaptation record</div>
            <textarea data-testid="diet-note-what" rows={2} value={note.what_changed} onChange={(e) => setNote({ ...note, what_changed: e.target.value })} placeholder="What changed?" className="w-full border border-[#E5E2D9] rounded-lg px-3 py-2 text-sm bg-[#F7F5F0] resize-none" />
            <textarea rows={2} value={note.why} onChange={(e) => setNote({ ...note, why: e.target.value })} placeholder="Why?" className="mt-2 w-full border border-[#E5E2D9] rounded-lg px-3 py-2 text-sm bg-[#F7F5F0] resize-none" />
            <button data-testid="diet-note-save" onClick={addNote} className="mt-2 rounded-full px-5 py-2 bg-[#2D372B] text-white text-sm font-medium">Record adaptation</button>
            <ul className="mt-4 space-y-2 max-h-48 overflow-y-auto">
              {(d.notes || []).map((n) => (
                <li key={n.id} className="text-xs text-[#5C665A] border-b border-[#F0EEE6] pb-1.5">
                  <span className="text-[#2D372B] font-medium">{n.what_changed}</span>{n.why ? ` — ${n.why}` : ""} · {n.approved_by} · {new Date(n.created_at).toLocaleDateString()}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5">
            <div className="font-medium mb-3">Assessment</div>
            {d.assessment ? (
              <div className="text-sm text-[#5C665A] space-y-1.5 max-h-72 overflow-y-auto" data-testid="diet-assessment">
                {Object.entries({ ...d.assessment.about, goal: d.assessment.goal?.primary, ...d.assessment.lifestyle, ...d.assessment.food, ...d.assessment.activity, typical_day: d.assessment.current_nutrition?.typical_day, ...d.assessment.health })
                  .filter(([, v]) => v && typeof v !== "object")
                  .map(([k, v]) => <div key={k}><span className="text-[#9AA294]">{k.replace(/_/g, " ")}:</span> {String(v)}</div>)}
              </div>
            ) : <p className="text-sm text-[#5C665A]">Awaiting onboarding.</p>}
          </div>
          <div className="bg-white border border-[#E5E2D9] rounded-2xl p-5">
            <div className="font-medium mb-3">Progress & payment</div>
            <ul className="text-sm text-[#5C665A] space-y-1.5 max-h-48 overflow-y-auto">
              {[...(d.progress || [])].reverse().map((e) => (
                <li key={e.id}>{new Date(e.created_at).toLocaleDateString()} — {e.weight_kg ? `${e.weight_kg} kg` : ""}{e.waist_cm ? ` · ${e.waist_cm} cm` : ""}{e.note ? ` · ${e.note}` : ""}</li>
              ))}
              {(d.progress || []).length === 0 && <li>No entries yet.</li>}
            </ul>
            {d.order && <div className="mt-3 pt-3 border-t border-[#F0EEE6] text-xs text-[#5C665A]">Paid ₹{d.order.price_inr} ({d.order.display_currency} {d.order.display_price} · {d.order.pricing_region}) · {new Date(d.order.created_at).toLocaleDateString()}</div>}
          </div>
        </div>

        <FoodTracker api={api} clientId={sel} />
        <GlobalCuration api={api} clientId={sel} assessment={d.assessment} onDone={() => loadDetail(sel)} />
      </div>
    );
  }

  return (
    <div data-testid="diet-admin-list">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-serif-display text-2xl tracking-tight">JAY ADMIN</div>
          <p className="text-sm text-[#5C665A]">Clients · {clients.length}</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-xs text-[#5C665A] hover:text-[#2D372B]"><RefreshCw size={12} /> Refresh</button>
      </div>
      <PricePlansCard api={api} />
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="trial-stats">
          {[
            ["Trials started", stats.started, "trial-stat-started"],
            ["Trial active", stats.active, "trial-stat-active"],
            ["Trial finished", stats.finished, "trial-stat-finished"],
            ["Upgraded to paid", stats.upgraded, "trial-stat-upgraded"],
            ["Conversion", `${stats.conversion_pct}%`, "trial-stat-conversion"],
          ].map(([l, v, tid]) => (
            <div key={l} className="bg-white border border-[#E5E2D9] rounded-2xl px-4 py-3" data-testid={tid}>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#9AA294]">{l}</div>
              <div className="font-serif-display text-2xl mt-0.5">{v}</div>
            </div>
          ))}
        </div>
      )}
      {clients.length === 0 ? (
        <p className="text-sm text-[#5C665A] py-10 text-center">No JAY DIETS clients yet.</p>
      ) : (
        <div className="bg-white border border-[#E5E2D9] rounded-2xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="diet-clients-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#9AA294] border-b border-[#E5E2D9]">
                <th className="px-5 py-3 font-medium">Client</th>
                <th className="px-5 py-3 font-medium">Programme</th>
                <th className="px-5 py-3 font-medium">Phase</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Next Review</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} data-testid="diet-client-row" onClick={() => open(c)}
                  className="border-b border-[#F0EEE6] last:border-0 cursor-pointer hover:bg-[#F7F5F0] transition-colors">
                  <td className="px-5 py-3.5 font-medium text-[#2D372B]">
                    {c.name}
                    {c.programme?.trial && <span className="ml-2 rounded-full bg-[#89937C]/15 text-[#5C665A] text-[10px] font-semibold px-2 py-0.5 align-middle">TRIAL</span>}
                    {c.professional_review && <Flag size={11} className="inline ml-2 text-[#A05252]" />}
                  </td>
                  <td className="px-5 py-3.5 text-[#5C665A]">{c.programme?.name || "—"}</td>
                  <td className="px-5 py-3.5 text-[#5C665A] uppercase">
                    {c.phase?.name || "—"}
                    {c.phase_manual && <span className="ml-1.5 normal-case rounded-full bg-[#AD6A48]/10 text-[#AD6A48] text-[9px] font-semibold px-1.5 py-0.5 align-middle">manual</span>}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="inline-block h-3.5 w-3.5 rounded-full" title={c.status}
                      style={{ background: `radial-gradient(circle at 35% 30%, ${STATUS_DOT[c.status] || "#9AA294"}dd, ${STATUS_DOT[c.status] || "#9AA294"})` }}
                      data-testid={`status-dot-${c.status}`} />
                  </td>
                  <td className="px-5 py-3.5 text-[#5C665A]">{c.next_review ? fmtShort(c.next_review) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
